import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Bell,
  Check,
  ChevronRight,
  Code2,
  Component,
  Copy,
  ExternalLink,
  FileText,
  Info,
  Layers3,
  Moon,
  Palette,
  Search,
  Save,
  Sun,
  Trash2,
  UserPlus,
} from 'lucide-react';
import BtnTaratic from '../../components/ui/BtnTaratic';
import ConsultedDataViewer from '../../components/ui/ConsultedDataViewer';
import { GhostInput } from '../../components/ui/GhostInput';
import { GhostSelect, type SelectOption } from '../../components/ui/GhostSelect';
import GhostSwitch from '../../components/ui/GhostSwitch';
import DefaultCard, { DEFAULT_CARD_CLASSES } from '../../components/ui/DefaultCard';
import EditorCard from '../../components/ui/EditorCard';
import { SlideOver } from '../../components/ui/SlideOver';
import { SlidingPills, type TabOption, type TabSize } from '../../components/ui/SlidingPills';
import { TagBadge } from '../../components/ui/TagBadge';
import StatusLabel from '../../components/ui/StatusLabel';
import StatusNotice from '../../components/ui/StatusNotice';
import { useDeleteConfirmation } from '../../components/ui/DeleteConfirmationProvider';
import { useNotifications } from '../../components/ui/notifications';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { uiActions, type ThemeMode } from '../../store/ui/uiSlice';
import type { BtnTaraticColor, BtnTaraticSize } from '../../contracts/uiContracts';
import './admin-design-system.css';

// ESLint's base rule treats TypeScript call-signature parameters as runtime variables.
// eslint-disable-next-line no-unused-vars
type ThemeRenderer = (theme: ThemeMode) => ReactNode;

const BUTTON_COLORS: Array<{ color: BtnTaraticColor; value: string }> = [
  { color: 'blue', value: 'var(--btn-blue)' },
  { color: 'green', value: 'var(--btn-green)' },
  { color: 'orange', value: 'var(--btn-orange)' },
  { color: 'red', value: 'var(--btn-red)' },
  { color: 'purple', value: 'var(--btn-purple)' },
  { color: 'indigo', value: 'var(--btn-indigo)' },
  { color: 'pink', value: 'var(--btn-pink)' },
  { color: 'black', value: 'var(--btn-black)' },
  { color: 'outline', value: 'transparent' },
  { color: 'ghost-dark', value: 'rgba / theme-aware' },
];

const TOKEN_SWATCHES = [
  { name: 'Blue 550', css: '--color-blue-550', value: '#0066FF' },
  { name: 'Green 550', css: '--color-green-550', value: '#1CB454' },
  { name: 'Orange 550', css: '--color-orange-550', value: '#F16611' },
  { name: 'Red 550', css: '--color-red-550', value: '#EE2E34' },
  { name: 'Purple 550', css: '--color-purple-550', value: '#834DF0' },
  { name: 'Pink 550', css: '--color-pink-550', value: '#E43788' },
  { name: 'Teal 550', css: '--color-teal-550', value: '#11A697' },
  { name: 'Black 900', css: '--color-black-900', value: '#0F111A' },
];

const SELECT_OPTIONS: SelectOption[] = [
  { value: 'analytics', label: 'Analytics', subtitle: 'Charts and reporting', icon: <Layers3 size={15} /> },
  { value: 'commerce', label: 'Commerce', subtitle: 'Products and orders', icon: <Component size={15} /> },
  { value: 'content', label: 'Content', subtitle: 'Documents and media', icon: <FileText size={15} /> },
];

const PILL_OPTIONS: TabOption[] = [
  { id: 'preview', label: 'Preview', customIcon: <Palette /> },
  { id: 'code', label: 'Code', customIcon: <Code2 /> },
  { id: 'notes', label: 'Notes', customIcon: <FileText /> },
];

const CONSULTED_DATA = {
  result: {
    file_name: 'design-system-guidelines.pdf',
    file_url: 'https://example.com/design-system-guidelines.pdf',
    chunks: [
      'Buttons use semantic color variants and a shared inner-glow interaction.',
      'Theme-aware controls must remain legible on light and dark surfaces.',
    ],
  },
};

const COMPONENT_INDEX = [
  ['foundations', 'Foundations', 'Colors & typography'],
  ['interface-card', 'Default card / div', 'Surfaces'],
  ['btn-taratic', 'BtnTaratic', 'Buttons'],
  ['form-controls', 'Ghost controls', 'Inputs, select & switch'],
  ['sliding-pills', 'SlidingPills', 'Navigation'],
  ['tag-badge', 'TagBadge', 'Data display'],
  ['status-feedback', 'StatusNotice & StatusLabel', 'Feedback'],
  ['consulted-data', 'ConsultedDataViewer', 'Data display'],
  ['editor-card', 'EditorCard', 'Create & edit forms'],
  ['overlays', 'Overlays & feedback', 'SlideOver, notifications & confirmation'],
] as const;

function PreviewSurface({ theme, children }: { theme: ThemeMode; children: ReactNode }) {
  return (
    <div
      className="design-preview-surface page-theme-surface"
      data-theme={theme}
      data-design-theme={theme}
    >
      <div className="design-preview-label">
        {theme === 'light' ? <Sun size={13} /> : <Moon size={13} />}
        {theme}
      </div>
      <div className="design-preview-content">{children}</div>
    </div>
  );
}

function ThemePreview({ theme, children }: { theme: ThemeMode; children: ThemeRenderer }) {
  return (
    <div className="design-preview-grid">
      <PreviewSurface theme={theme}>{children(theme)}</PreviewSurface>
    </div>
  );
}

function ShowcaseCard({
  id,
  title,
  source,
  description,
  theme,
  children,
}: {
  id: string;
  title: string;
  source: string;
  description: string;
  theme: ThemeMode;
  children: ThemeRenderer;
}) {
  return (
    <article id={id} className="design-showcase-card scroll-mt-28" data-component-name={title.toLowerCase()}>
      <div className="design-card-heading">
        <div>
          <div className="flex items-center gap-2">
            <Component size={16} className="text-blue-400" />
            <h2>{title}</h2>
          </div>
          <p>{description}</p>
        </div>
        <code>{source}</code>
      </div>
      <ThemePreview theme={theme}>{children}</ThemePreview>
    </article>
  );
}

function InteractiveComponents({ theme }: { theme: ThemeMode }) {
  const [selected, setSelected] = useState('analytics');
  const [multiSelected, setMultiSelected] = useState<string[]>(['analytics', 'content']);
  const [enabled, setEnabled] = useState(true);
  return (
    <div className="grid w-full gap-5 md:grid-cols-2">
      <label className="design-field-label">
        GhostInput
        <GhostInput placeholder="Search components…" aria-label={`Ghost input ${theme}`} />
      </label>
      <label className="design-field-label">
        Disabled state
        <GhostInput value="Read-only value" disabled aria-label={`Disabled ghost input ${theme}`} readOnly />
      </label>
      <div className="design-field-label">
        GhostSelect · searchable
        <GhostSelect
          options={SELECT_OPTIONS}
          value={selected}
          onChange={setSelected}
          searchable
          variant={theme}
        />
      </div>
      <div className="design-field-label">
        GhostSelect · multiple
        <GhostSelect
          options={SELECT_OPTIONS}
          value={multiSelected}
          onChange={setMultiSelected}
          multiple
          variant={theme}
        />
      </div>
      <div className="md:col-span-2 flex flex-wrap items-center gap-6 pt-1">
        {(['sm', 'md', 'lg'] as const).map((size) => (
          <GhostSwitch
            key={size}
            checked={enabled}
            onChange={setEnabled}
            size={size}
            variant={theme}
            label={`${size.toUpperCase()} ${enabled ? 'on' : 'off'}`}
          />
        ))}
        <GhostSwitch checked={false} onChange={() => {}} disabled variant={theme} label="Disabled" />
      </div>
    </div>
  );
}

function PillComponents({ theme }: { theme: ThemeMode }) {
  const [active, setActive] = useState('preview');
  return (
    <div className="flex w-full flex-col items-start gap-5 overflow-x-auto pb-1">
      <div className="flex items-center gap-4">
        <span className="design-measure-label">icons</span>
        <SlidingPills options={PILL_OPTIONS} activeId={active} onChange={setActive} size="sm" variant={theme} iconOnly />
      </div>
      {(['xs', 'sm', 'md'] as TabSize[]).map((size) => (
        <div key={size} className="flex items-center gap-4">
          <span className="design-measure-label">{size}</span>
          <SlidingPills options={PILL_OPTIONS} activeId={active} onChange={setActive} size={size} variant={theme} />
        </div>
      ))}
    </div>
  );
}

function DefaultInterfaceCardPreview({ theme, showCss }: { theme: ThemeMode; showCss: boolean }) {
  return (
    <div className="w-full max-w-2xl space-y-4">
      <DefaultCard dataUi="default-interface-card" data-theme={theme} className="w-full p-6">
        <div className="flex items-start gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-500/15 text-blue-400">
            <Layers3 size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-base font-semibold text-white">Default interface card</h3>
              <span className="rounded-full bg-green-500/20 px-2.5 py-1 text-xs font-medium text-green-400">
                Active
              </span>
            </div>
            <p className="mt-2 text-sm leading-6 text-white/60">
              Use this surface for dashboard panels, settings groups, summaries, and other standard interface content.
            </p>
            <div className="mt-5 flex flex-wrap gap-x-8 gap-y-3 border-t border-white/10 pt-4 text-sm">
              <div>
                <p className="text-xs text-white/50">Theme</p>
                <p className="mt-1 font-medium capitalize text-white">{theme}</p>
              </div>
              <div>
                <p className="text-xs text-white/50">Surface</p>
                <p className="mt-1 font-medium text-white">Default card / div</p>
              </div>
            </div>
          </div>
        </div>
      </DefaultCard>

      {showCss && (
        <code className="block overflow-x-auto rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-[11px] leading-5 text-white/60">
          {DEFAULT_CARD_CLASSES}
        </code>
      )}
    </div>
  );
}

function EditorCardPreview({ theme }: { theme: ThemeMode }) {
  const [type, setType] = useState('commerce');

  return (
    <EditorCard
      title="Create workspace"
      icon={<UserPlus size={19} className="text-green-400" />}
      variant={theme}
      maxWidth="full"
      onClose={() => {}}
      closeLabel="Close preview"
      className="!max-h-none"
      footer={(
        <>
          <BtnTaratic color="ghost-dark">Cancel</BtnTaratic>
          <BtnTaratic color="blue"><Save size={15} /> Create workspace</BtnTaratic>
        </>
      )}
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <label className="design-field-label md:col-span-2">
          Name
          <GhostInput placeholder="Workspace name" aria-label="Workspace name preview" />
        </label>
        <label className="design-field-label">
          Owner email
          <GhostInput placeholder="owner@example.com" aria-label="Owner email preview" />
        </label>
        <div className="design-field-label">
          Workspace type
          <GhostSelect options={SELECT_OPTIONS} value={type} onChange={setType} variant={theme} />
        </div>
      </div>
    </EditorCard>
  );
}

function OverlayControls({ theme }: { theme: ThemeMode }) {
  const [isSlideOverOpen, setSlideOverOpen] = useState(false);
  const notifications = useNotifications();
  const confirmDelete = useDeleteConfirmation();

  const openConfirmation = async () => {
    const confirmed = await confirmDelete({
      title: 'Delete component draft?',
      message: 'This is a safe preview. No real data will be removed.',
      confirmLabel: 'Delete draft',
      cancelLabel: 'Keep draft',
    });
    if (confirmed) notifications.success('Draft deleted', 'The confirmation component completed its flow.');
  };

  return (
    <div className="flex w-full flex-wrap gap-3">
      <BtnTaratic color="blue" onClick={() => setSlideOverOpen(true)}>
        <Layers3 size={15} /> Open SlideOver
      </BtnTaratic>
      <BtnTaratic color="ghost-dark" onClick={() => notifications.info('Design preview', `${theme} theme notification preview`)}>
        <Bell size={15} /> Notification
      </BtnTaratic>
      <BtnTaratic color="red" onClick={openConfirmation}>
        <Trash2 size={15} /> Confirmation
      </BtnTaratic>
      <SlideOver
        isOpen={isSlideOverOpen}
        onClose={() => setSlideOverOpen(false)}
        title="Component details"
        footer={(
          <>
            <BtnTaratic color="ghost-dark" onClick={() => setSlideOverOpen(false)}>Cancel</BtnTaratic>
            <BtnTaratic color="green" onClick={() => setSlideOverOpen(false)}>Save changes</BtnTaratic>
          </>
        )}
      >
        <div data-theme={theme} className="space-y-5 text-white">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
            <p className="text-sm text-white/60">SlideOver.tsx</p>
            <h3 className="mt-1 text-lg font-semibold">A real interactive overlay</h3>
            <p className="mt-2 text-sm leading-6 text-white/70">
              Use this area to validate long-form content, footer actions, focus behavior, and responsive width.
            </p>
          </div>
          <GhostInput placeholder="Component name" />
          <GhostSwitch checked onChange={() => {}} variant="dark" label="Publish when saved" />
        </div>
      </SlideOver>
    </div>
  );
}

export default function AdminDesignSystemPage() {
  const dispatch = useAppDispatch();
  const appTheme = useAppSelector((state) => state.ui.theme);
  const [theme, setTheme] = useState<ThemeMode>(appTheme);
  const [query, setQuery] = useState('');
  const [showCss, setShowCss] = useState(true);

  useEffect(() => {
    if (theme !== appTheme) dispatch(uiActions.setTheme(theme));
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    document.body.dataset.theme = theme;
  }, [appTheme, dispatch, theme]);

  const visibleSections = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return new Set<string>(COMPONENT_INDEX.map(([id]) => id));
    return new Set<string>(
      COMPONENT_INDEX
        .filter(([, name, category]) => `${name} ${category}`.toLowerCase().includes(normalized))
        .map(([id]) => id),
    );
  }, [query]);

  const sectionVisible = (id: string) => visibleSections.has(id);

  return (
    <div className="design-system-page min-h-full" data-ui="admin-design-system-page">
      <header className="design-system-hero">
        <div className="design-hero-copy">
          <div className="design-eyebrow"><Palette size={14} /> Admin · App design</div>
          <h1>Component studio</h1>
          <p>Inspect the shared UI library, compare themes, exercise interactions, and review the CSS tokens behind every surface.</p>
        </div>
        <div className="design-hero-stats" aria-label="Design system summary">
          <span><strong>13</strong> visual components</span>
          <span><strong>2</strong> themes</span>
          <span><strong>12</strong> UI modules</span>
        </div>
      </header>

      <div className="design-toolbar">
        <div className="design-search">
          <Search size={16} />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Find a component…" aria-label="Find a component" />
          {query && <button type="button" onClick={() => setQuery('')} aria-label="Clear search">×</button>}
        </div>
        <div className="design-mode-switch" aria-label="Application theme">
          {([
            ['light', <Sun size={14} />, 'Light'],
            ['dark', <Moon size={14} />, 'Dark'],
          ] as Array<[ThemeMode, ReactNode, string]>).map(([value, icon, label]) => (
            <button key={value} type="button" data-active={theme === value} onClick={() => setTheme(value)}>
              {icon}{label}
            </button>
          ))}
        </div>
        <GhostSwitch checked={showCss} onChange={setShowCss} variant="dark" size="sm" label="CSS values" />
      </div>

      <div className="design-system-layout">
        <aside className="design-index">
          <p>On this page</p>
          <nav>
            {COMPONENT_INDEX.map(([id, name, category]) => (
              <a key={id} href={`#${id}`} data-hidden={!visibleSections.has(id)}>
                <span>{name}<small>{category}</small></span><ChevronRight size={14} />
              </a>
            ))}
          </nav>
          <div className="design-index-note"><Info size={14} /> Components are rendered live from <code>components/ui</code>.</div>
        </aside>

        <main className="design-catalog">
          {sectionVisible('foundations') && (
            <section id="foundations" className="design-foundations scroll-mt-28">
              <div className="design-section-title">
                <div><span>01</span><h2>Foundations</h2></div>
                <p>Generated theme tokens currently available to every component.</p>
              </div>
              <div className="design-token-grid">
                {TOKEN_SWATCHES.map((token) => (
                  <button key={token.css} type="button" className="design-token" onClick={() => navigator.clipboard?.writeText(`var(${token.css})`)} title="Copy CSS variable">
                    <span className="design-token-color" style={{ background: token.value }} />
                    <span><strong>{token.name}</strong>{showCss && <><code>{token.css}</code><small>{token.value}</small></>}</span>
                    <Copy size={13} />
                  </button>
                ))}
              </div>
              <div className="design-type-specimen">
                <div><span>Display</span><strong>Aa</strong></div>
                <div><span>Heading</span><h3>Build consistent interfaces.</h3></div>
                <div><span>Body</span><p>Inter is the application typeface. Clear hierarchy and calm spacing keep dense admin workflows readable.</p></div>
                <div><span>Code</span><code>font-family: 'Inter', sans-serif;</code></div>
              </div>
            </section>
          )}

          {sectionVisible('interface-card') && (
            <ShowcaseCard
              id="interface-card"
              title="Default card / div"
              source="DefaultCard.tsx"
              description="The default application surface for dashboard panels, settings groups, summaries, and general interface content."
              theme={theme}
            >
              {(theme) => <DefaultInterfaceCardPreview theme={theme} showCss={showCss} />}
            </ShowcaseCard>
          )}

          {sectionVisible('btn-taratic') && (
            <ShowcaseCard id="btn-taratic" title="BtnTaratic" source="BtnTaratic.tsx" description="All semantic colors, sizes, loading, disabled, and icon-only states." theme={theme}>
              {() => (
                <div className="w-full space-y-6">
                  <div className="flex flex-wrap gap-3">
                    {BUTTON_COLORS.map(({ color, value }) => (
                      <div key={color} className="design-button-sample">
                        <BtnTaratic color={color}>{color}</BtnTaratic>
                        {showCss && <code>{value}</code>}
                      </div>
                    ))}
                  </div>
                  <div className="design-state-row">
                    {(['xs', 'sm', 'md', 'lg', 'xl'] as BtnTaraticSize[]).map((size) => <BtnTaratic key={size} size={size} color="blue">{size}</BtnTaratic>)}
                    <BtnTaratic color="blue" loading>Loading</BtnTaratic>
                    <BtnTaratic color="blue" disabled>Disabled</BtnTaratic>
                    <BtnTaratic color="purple" iconOnly aria-label="Icon-only button"><Check size={17} /></BtnTaratic>
                  </div>
                </div>
              )}
            </ShowcaseCard>
          )}

          {sectionVisible('form-controls') && (
            <ShowcaseCard id="form-controls" title="GhostInput · GhostSelect · GhostSwitch" source="Ghost*.tsx" description="Form controls with live state, searchable options, multi-select, and every switch size." theme={theme}>
              {(theme) => <InteractiveComponents theme={theme} />}
            </ShowcaseCard>
          )}

          {sectionVisible('sliding-pills') && (
            <ShowcaseCard id="sliding-pills" title="SlidingPills" source="SlidingPills.tsx" description="Responsive segmented navigation across text, icon, and size configurations." theme={theme}>
              {(theme) => <PillComponents theme={theme} />}
            </ShowcaseCard>
          )}

          {sectionVisible('tag-badge') && (
            <ShowcaseCard id="tag-badge" title="TagBadge" source="TagBadge.tsx" description="Semantic tag colors, optional folder context, custom children, and three sizes." theme={theme}>
              {() => (
                <div className="flex w-full flex-wrap items-center gap-3">
                  <TagBadge tag={{ name: 'Priority', color: '#EF4444' }} size="xs" />
                  <TagBadge tag={{ name: 'Qualified', color: '#22C55E' }} size="sm" />
                  <TagBadge tag={{ name: 'Follow up', color: '#8B5CF6' }} folderName="Sales" size="md" />
                  <TagBadge tag={{ name: 'Interactive', color: '#0EA5E9' }} onClick={() => {}}><ExternalLink size={11} /></TagBadge>
                </div>
              )}
            </ShowcaseCard>
          )}

          {sectionVisible('status-feedback') && (
            <ShowcaseCard id="status-feedback" title="StatusNotice / StatusLabel" source="StatusNotice.tsx / StatusLabel.tsx" description="High-contrast semantic feedback containers and compact labels for application status." theme={theme}>
              {() => (
                <div className="w-full space-y-4">
                  <div className="grid gap-3 md:grid-cols-2">
                    <StatusNotice variant="success" role="status">Workflow guardado.</StatusNotice>
                    <StatusNotice variant="warning">Review this configuration before continuing.</StatusNotice>
                    <StatusNotice variant="danger" role="alert">The action could not be completed.</StatusNotice>
                    <StatusNotice variant="info">An update is available.</StatusNotice>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <StatusLabel variant="info">CITA</StatusLabel>
                    <StatusLabel variant="success">ACTIVO</StatusLabel>
                    <StatusLabel variant="warning">PENDIENTE</StatusLabel>
                    <StatusLabel variant="danger">ERROR</StatusLabel>
                    <StatusLabel variant="neutral">PAUSADO</StatusLabel>
                  </div>
                </div>
              )}
            </ShowcaseCard>
          )}

          {sectionVisible('consulted-data') && (
            <ShowcaseCard id="consulted-data" title="ConsultedDataViewer" source="ConsultedDataViewer.tsx" description="Expandable document sources with human-readable and raw JSON views." theme={theme}>
              {() => <div className="design-consulted-data w-full"><ConsultedDataViewer data={CONSULTED_DATA} /></div>}
            </ShowcaseCard>
          )}

          {sectionVisible('editor-card') && (
            <ShowcaseCard id="editor-card" title="EditorCard" source="EditorCard.tsx" description="The shared create/edit form shell used by production modals, with live header, body, actions, and theme behavior." theme={theme}>
              {(theme) => <EditorCardPreview theme={theme} />}
            </ShowcaseCard>
          )}

          {sectionVisible('overlays') && (
            <ShowcaseCard id="overlays" title="SlideOver · NotificationProvider · DeleteConfirmationProvider" source="SlideOver.tsx · *Provider.tsx" description="Open each production overlay to inspect its focus, motion, copy, and action states." theme={theme}>
              {(theme) => <OverlayControls theme={theme} />}
            </ShowcaseCard>
          )}

          {visibleSections.size === 0 && (
            <div className="design-empty-state"><Search size={24} /><h2>No matching components</h2><p>Try “button”, “overlay”, “data”, or “navigation”.</p></div>
          )}
        </main>
      </div>
    </div>
  );
}
